import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SectionDecor } from "@/components/Decor";
import { MapPin, Calendar, Users, Tag, Baby, Rocket, School, Landmark, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import ReservationDialog from "@/components/ReservationDialog";

const ACADEMY_IMG = "https://static.prod-images.emergentagent.com/jobs/1304f033-c917-4974-91b0-9d4153e7c191/images/47ccafbaaf4680901ba907b81463c81d171d6bc642300ebac6a9f7a403feed3d.jpeg";

const STAGE_OFFERS = [
  { id: "eveil", icon: Baby, color: "bg-rose-100 text-rose-600", title: "Stages d'éveil numérique pour les 4-7 ans", description: "Votre enfant rêve d'explorer le monde qui l'entoure. Faites-lui découvrir la magie du numérique au travers d'activités ludiques adaptées aux tout-petits, sans écran ou avec des outils spécialement conçus pour eux." },
  { id: "stage812", icon: Rocket, color: "bg-amber-100 text-amber-600", title: "Stages numériques pour les 8-12 ans", description: "Offrez à votre enfant une semaine inoubliable ponctuée de défis de programmation, de jeux d'algorithmes et de découvertes surprenantes sur l'intelligence artificielle !" },
];

const SCHOOL_OFFERS = [
  { id: "animations", icon: School, color: "bg-sky-100 text-sky-600", title: "Animations en écoles", description: "Invitez un animateur Kwarizmix dans votre classe et faites découvrir à vos élèves le monde passionnant du numérique, de la pensée computationnelle et des enjeux de l'IA." },
  { id: "excursions", icon: Landmark, color: "bg-emerald-100 text-emerald-600", title: "Excursions scolaires", description: "Emmenez vos élèves dans un lieu d'exception pour vivre une excursion scolaire au carrefour du numérique, de l'histoire des technologies et de la culture." },
];

function OfferCard({ offer, index = 0 }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: (index % 2) * 0.08 }}
      className="bg-white rounded-3xl border border-slate-100 shadow-sm p-7 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] flex flex-col"
      data-testid={`offer-${offer.id}`}
    >
      <span className={`grid place-items-center w-14 h-14 rounded-2xl mb-4 ${offer.color}`}>
        <offer.icon className="w-7 h-7" />
      </span>
      <h3 className="font-heading font-extrabold text-xl text-slate-900">{offer.title}</h3>
      <p className="text-sm text-slate-500 mt-2 flex-1">{offer.description}</p>
      <Link to="/nos-activites" data-testid={`offer-link-${offer.id}`} className="inline-flex items-center gap-1.5 mt-5 text-sky-600 font-bold hover:gap-2.5 transition-all">
        En savoir plus <ArrowRight className="w-4 h-4" />
      </Link>
    </motion.div>
  );
}

export default function Academy() {
  const [sessions, setSessions] = useState([]);

  useEffect(() => {
    api.get("/academy").then((r) => setSessions(r.data)).catch(() => {});
  }, []);

  return (
    <div>
      <section className="relative overflow-hidden bg-gradient-to-b from-amber-50 to-white py-16">
        <SectionDecor />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600">Stages</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-2">
              Bienvenue dans notre univers !
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Kwarizmix est le compagnon de route idéal pour aider votre enfant à
              se construire et à révéler les talents qui sommeillent en lui. Durant
              toute son enfance, nos animateurs sont à ses côtés pour accompagner son
              développement et titiller sa curiosité numérique. Faites-lui découvrir
              le monde passionnant du numérique, de la programmation et de
              l'intelligence artificielle — à la maison, en stage de vacances ou à
              l'école. Une animation anniversaire détonante, un stage numérique
              palpitant, un atelier ludico-pédagogique dans sa classe : faites votre
              choix !
            </p>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-[2rem] overflow-hidden shadow-2xl shadow-amber-500/20 -rotate-2">
            <img src={ACADEMY_IMG} alt="Stage Kwarizmix" className="w-full h-[360px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Offres : En stage / A l'ecole */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-3 mb-8">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-amber-100 text-amber-600"><Rocket className="w-6 h-6" /></span>
          <h2 className="font-heading font-extrabold text-3xl text-slate-900">En stage</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {STAGE_OFFERS.map((o, i) => <OfferCard key={o.id} offer={o} index={i} />)}
        </div>

        <div className="flex items-center gap-3 mt-16 mb-8">
          <span className="grid place-items-center w-12 h-12 rounded-2xl bg-sky-100 text-sky-600"><School className="w-6 h-6" /></span>
          <h2 className="font-heading font-extrabold text-3xl text-slate-900">À l'école</h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {SCHOOL_OFFERS.map((o, i) => <OfferCard key={o.id} offer={o} index={i} />)}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <h2 className="font-heading font-extrabold text-3xl text-slate-900 mb-8">Planning des sessions</h2>
        <div className="grid md:grid-cols-2 gap-6">
          {sessions.map((s, i) => (
            <motion.div
              key={s.id}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: (i % 2) * 0.08 }}
              className="bg-white rounded-3xl border border-slate-100 shadow-sm p-7 hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow]"
              data-testid={`academy-session-${s.id}`}
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <span className="inline-block px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs font-bold mb-3">
                    {s.period}
                  </span>
                  <h3 className="font-heading font-extrabold text-xl text-slate-900">{s.title}</h3>
                </div>
                <span className="font-heading font-black text-2xl text-sky-500 shrink-0">{s.price.toFixed(0)} €</span>
              </div>
              <p className="text-sm text-slate-500 mt-3">{s.description}</p>
              <div className="grid grid-cols-2 gap-3 mt-5 text-sm">
                <span className="inline-flex items-center gap-2 text-slate-600"><Calendar className="w-4 h-4 text-sky-500" /> {s.dates}</span>
                <span className="inline-flex items-center gap-2 text-slate-600"><MapPin className="w-4 h-4 text-sky-500" /> {s.location}</span>
                <span className="inline-flex items-center gap-2 text-slate-600"><Users className="w-4 h-4 text-sky-500" /> {s.ages}</span>
                <span className="inline-flex items-center gap-2 text-slate-600"><Tag className="w-4 h-4 text-sky-500" /> {s.spots} places</span>              </div>
              <div className="mt-6 flex flex-col gap-2">
                <Link to={`/academie/${s.id}`} data-testid={`academy-detail-link-${s.id}`} className="w-full text-center py-3 rounded-full border border-slate-200 text-slate-700 font-bold hover:border-amber-400 hover:text-amber-600 transition-colors">
                  Voir le programme détaillé
                </Link>
                <ReservationDialog
                  kind="stage"
                  itemId={s.id}
                  itemTitle={s.title}
                  trigger={
                    <button
                      data-testid={`reserve-academy-${s.id}`}
                      className="w-full py-3 rounded-full bg-slate-900 text-white font-bold hover:bg-sky-600 transition-colors"
                    >
                      Réserver une place
                    </button>
                  }
                />
              </div>
            </motion.div>
          ))}
        </div>
      </section>
    </div>
  );
}
